<template>
  <div class="raw-material-analysis-report-view pa-4">
    <!-- 1. Encabezado de Título Neutro -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="d-flex align-center py-4 px-5 flex-wrap">
        <v-avatar color="grey lighten-4" size="48" class="mr-4">
          <v-icon color="primary" large>mdi-file-chart-outline</v-icon>
        </v-avatar>
        <div>
          <h2 class="text-h6 font-weight-black grey--text text--darken-4 mb-0">
            Reporte de Análisis de Materia Prima
          </h2>
          <span class="text-caption grey--text text--darken-2">
            Consolidado independiente de resultados y dictámenes de Metales Pesados y Pesticidas por lote y campaña.
          </span>
        </div>
      </v-card-title>

      <!-- Selector de Pestañas Principal -->
      <v-divider></v-divider>
      <v-tabs
        v-model="activeTab"
        color="primary"
        slider-color="primary"
        class="px-4"
      >
        <v-tab class="font-weight-bold text-none py-3">
          <v-icon left small>mdi-chemical-weapon</v-icon>
          Metales Pesados
          <v-chip x-small class="ml-2 grey lighten-3 font-weight-bold">
            {{ metalsList.length }}
          </v-chip>
        </v-tab>

        <v-tab class="font-weight-bold text-none py-3">
          <v-icon left small>mdi-bug-outline</v-icon>
          Pesticidas y Trazas
          <v-chip x-small class="ml-2 grey lighten-3 font-weight-bold">
            {{ pesticidesList.length }}
          </v-chip>
        </v-tab>
      </v-tabs>
    </v-card>

    <!-- 2. Contenido Independiente por Pestaña -->
    <v-tabs-items v-model="activeTab" class="bg-transparent">
      <!-- ========================================================================= -->
      <!-- ======================== TAB 1: METALES PESADOS ========================= -->
      <!-- ========================================================================= -->
      <v-tab-item>
        <!-- Filtros Metales Pesados -->
        <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
          <v-card-title class="py-3 px-5 d-flex align-center flex-wrap">
            <v-icon color="grey darken-3" small class="mr-2">mdi-filter-variant</v-icon>
            <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
              Filtros - Metales Pesados
            </span>
          </v-card-title>
          <v-divider></v-divider>

          <v-card-text class="py-4 px-5">
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4" lg="2">
                <s-date
                  label="Fecha Desde"
                  v-model="metalsFilters.BeginDate"
                  clearable
                />
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="2">
                <s-date
                  label="Fecha Hasta"
                  v-model="metalsFilters.EndDate"
                  clearable
                />
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <s-select-campaign-area
                  label="Campaña"
                  v-model="metalsFilters.CmaID"
                  typeArea="1"
                  clearable
                  noDefault
                />
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <s-select-lot
                  label="Sub Lote"
                  v-model="metalsFilters.FltID"
                  clearable
                  full
                  noDefault
                />
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="2">
                <s-select-definition
                  label="Tipo Cultivo"
                  :def="1173"
                  v-model="metalsFilters.TypeCultive"
                  clearable
                />
              </v-col>
            </v-row>

            <v-row dense class="mt-2">
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                class="mr-2 px-4 rounded-lg font-weight-bold white--text text-none"
                elevation="0"
                height="40"
                :loading="metalsLoading"
                @click="loadMetalsReport"
              >
                <v-icon left small>mdi-magnify</v-icon>
                Consultar Metales
              </v-btn>

              <v-btn
                color="success"
                class="px-4 rounded-lg font-weight-bold white--text text-none"
                elevation="0"
                height="40"
                :disabled="metalsList.length === 0"
                @click="exportMetalsExcel"
              >
                <v-icon left small>mdi-file-excel</v-icon>
                Exportar Excel
              </v-btn>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- KPIs Metales Pesados -->
        <v-row dense class="mb-4">
          <v-col cols="12" sm="6" md="3">
            <v-card class="rounded-xl pa-3 bg-white border-light" outlined elevation="0">
              <div class="d-flex align-center">
                <v-avatar color="grey lighten-4" size="42" class="mr-3">
                  <v-icon color="grey darken-3">mdi-flask-outline</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                    Total Evaluaciones
                  </div>
                  <div class="text-h5 font-weight-black grey--text text--darken-4">
                    {{ metalsList.length }}
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card class="rounded-xl pa-3 bg-white border-light" outlined elevation="0">
              <div class="d-flex align-center">
                <v-avatar color="green lighten-5" size="42" class="mr-3">
                  <v-icon color="green darken-2">mdi-check-decagram</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption text-uppercase font-weight-bold green--text text--darken-3">
                    Aprobados (Libre)
                  </div>
                  <div class="text-h5 font-weight-black green--text text--darken-3">
                    {{ metalsKpis.approvedCount }}
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card class="rounded-xl pa-3 bg-white border-light" outlined elevation="0">
              <div class="d-flex align-center">
                <v-avatar color="orange lighten-5" size="42" class="mr-3">
                  <v-icon color="orange darken-3">mdi-alert-circle-outline</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption text-uppercase font-weight-bold orange--text text--darken-3">
                    Restringidos / Rechazados
                  </div>
                  <div class="text-h5 font-weight-black orange--text text--darken-3">
                    {{ metalsKpis.restrictedCount }}
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card class="rounded-xl pa-3 bg-white border-light" outlined elevation="0">
              <div class="d-flex align-center">
                <v-avatar color="blue lighten-5" size="42" class="mr-3">
                  <v-icon color="blue darken-3">mdi-clock-check-outline</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption text-uppercase font-weight-bold blue--text text--darken-3">
                    Entregas a Tiempo
                  </div>
                  <div class="text-h5 font-weight-black blue--text text--darken-3">
                    {{ metalsKpis.onTimeCount }}
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Tabla Metales Pesados -->
        <v-card class="rounded-xl border-light bg-white" outlined elevation="0">
          <div class="pa-4 d-flex align-center justify-space-between flex-wrap">
            <div style="max-width: 380px; width: 100%;">
              <v-text-field
                v-model="metalsSearch"
                dense
                outlined
                hide-details
                placeholder="Buscar por lote, productor, sustancia..."
                prepend-inner-icon="mdi-magnify"
                clearable
                class="rounded-lg"
              />
            </div>
            <div class="text-caption grey--text text--darken-2 font-weight-bold mt-2 mt-sm-0">
              Mostrando {{ filteredMetalsList.length }} de {{ metalsList.length }} registros
            </div>
          </div>

          <v-data-table
            :headers="heavyMetalsHeaders"
            :items="filteredMetalsList"
            :loading="metalsLoading"
            dense
            class="elevation-0 report-table"
            :items-per-page="15"
            :footer-props="{
              'items-per-page-options': [10, 15, 25, 50, 100],
              'show-current-page': true,
            }"
          >
            <!-- Lote -->
            <template v-slot:[`item.FltID`]="{ item }">
              <span class="font-weight-black grey--text text--darken-4">
                {{ item.FltID || '-' }}
              </span>
            </template>

            <!-- Lote MP -->
            <template v-slot:[`item.LoteMp`]="{ item }">
              <span class="font-weight-bold grey--text text--darken-4">
                {{ item.LoteMp || '-' }}
              </span>
            </template>

            <!-- Sustancia -->
            <template v-slot:[`item.SubstancesName`]="{ item }">
              <span class="font-weight-bold primary--text">
                {{ item.SubstancesName || '-' }}
              </span>
            </template>

            <!-- Valor RMD -->
            <template v-slot:[`item.RmdValue`]="{ item }">
              <span class="font-weight-bold font-mono">
                {{ formatDecimal(item.RmdValue) }}
              </span>
            </template>

            <!-- Fechas -->
            <template v-slot:[`item.SampleSubmissionDate`]="{ item }">
              {{ formatDate(item.SampleSubmissionDate) }}
            </template>
            <template v-slot:[`item.LaboratoryEntryDate`]="{ item }">
              {{ formatDate(item.LaboratoryEntryDate) }}
            </template>
            <template v-slot:[`item.ProposedResultsDate`]="{ item }">
              {{ formatDate(item.ProposedResultsDate) }}
            </template>
            <template v-slot:[`item.ResultsIssueDate`]="{ item }">
              {{ formatDate(item.ResultsIssueDate) }}
            </template>

            <!-- Entrega -->
            <template v-slot:[`item.Entrega`]="{ item }">
              <v-chip
                x-small
                class="font-weight-bold text-uppercase"
                :color="item.Entrega && item.Entrega.toUpperCase().includes('TIEMPO') ? 'green lighten-5' : 'red lighten-5'"
                :text-color="item.Entrega && item.Entrega.toUpperCase().includes('TIEMPO') ? 'green darken-3' : 'red darken-3'"
              >
                {{ item.Entrega || '-' }}
              </v-chip>
            </template>

            <!-- Estado Dictamen -->
            <template v-slot:[`item.TypeStatusApprovalsName`]="{ item }">
              <v-chip
                small
                class="font-weight-bold text-uppercase"
                :color="getStatusColor(item.TypeStatusApprovalsName).bg"
                :text-color="getStatusColor(item.TypeStatusApprovalsName).text"
              >
                <v-icon left x-small>{{ getStatusColor(item.TypeStatusApprovalsName).icon }}</v-icon>
                {{ formatStatusText(item.TypeStatusApprovalsName) }}
              </v-chip>
            </template>

            <!-- Mercados No Conformes -->
            <template v-slot:[`item.FailedMarkets`]="{ item }">
              <div v-if="item.FailedMarkets" style="min-width: 130px;">
                <v-chip
                  v-for="(market, idx) in parseMarketList(item.FailedMarkets)"
                  :key="idx"
                  x-small
                  color="red lighten-5"
                  text-color="red darken-3"
                  class="font-weight-bold mb-1 mr-1 text-wrap"
                  style="height: auto; padding: 4px 8px;"
                >
                  {{ market }}
                </v-chip>
              </div>
              <span v-else class="grey--text text--lighten-1 font-italic">Ninguno</span>
            </template>

            <!-- Mercados Aptos -->
            <template v-slot:[`item.ApprovedMarkets`]="{ item }">
              <div v-if="item.ApprovedMarkets" style="min-width: 250px;">
                <v-chip
                  v-for="(market, idx) in parseMarketList(item.ApprovedMarkets)"
                  :key="idx"
                  x-small
                  color="green lighten-5"
                  text-color="green darken-3"
                  class="font-weight-bold mb-1 mr-1 text-wrap"
                  style="height: auto; padding: 4px 8px;"
                >
                  {{ market }}
                </v-chip>
              </div>
              <span v-else class="grey--text text--lighten-1 font-italic">Ninguno</span>
            </template>

            <!-- Observación -->
            <template v-slot:[`item.ObservationApproval`]="{ item }">
              <div v-if="item.ObservationApproval" class="grey--text text--darken-2 text-wrap" style="min-width: 260px; font-size: 0.8rem; line-height: 1.35;">
                {{ item.ObservationApproval }}
              </div>
              <span v-else class="grey--text text--lighten-1">-</span>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- ========================================================================= -->
      <!-- ==================== TAB 2: PESTICIDAS Y TRAZAS ========================= -->
      <!-- ========================================================================= -->
      <v-tab-item>
        <!-- Filtros Pesticidas -->
        <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
          <v-card-title class="py-3 px-5 d-flex align-center flex-wrap">
            <v-icon color="grey darken-3" small class="mr-2">mdi-filter-variant</v-icon>
            <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
              Filtros - Pesticidas y Trazas
            </span>
          </v-card-title>
          <v-divider></v-divider>

          <v-card-text class="py-4 px-5">
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4" lg="2">
                <s-date
                  label="Fecha Desde"
                  v-model="pesticidesFilters.BeginDate"
                  clearable
                />
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="2">
                <s-date
                  label="Fecha Hasta"
                  v-model="pesticidesFilters.EndDate"
                  clearable
                />
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <s-select-campaign-area
                  label="Campaña"
                  v-model="pesticidesFilters.CmaID"
                  typeArea="1"
                  clearable
                  noDefault
                />
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <s-select-lot
                  label="Sub Lote"
                  v-model="pesticidesFilters.FltID"
                  clearable
                  full
                  noDefault
                />
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="2">
                <s-select-definition
                  label="Tipo Cultivo"
                  :def="1173"
                  v-model="pesticidesFilters.TypeCultive"
                  clearable
                />
              </v-col>
            </v-row>

            <v-row dense class="mt-2">
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                class="mr-2 px-4 rounded-lg font-weight-bold white--text text-none"
                elevation="0"
                height="40"
                :loading="pesticidesLoading"
                @click="loadPesticidesReport"
              >
                <v-icon left small>mdi-magnify</v-icon>
                Consultar Pesticidas
              </v-btn>

              <v-btn
                color="success"
                class="px-4 rounded-lg font-weight-bold white--text text-none"
                elevation="0"
                height="40"
                :disabled="pesticidesList.length === 0"
                @click="exportPesticidesExcel"
              >
                <v-icon left small>mdi-file-excel</v-icon>
                Exportar Excel
              </v-btn>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- KPIs Pesticidas -->
        <v-row dense class="mb-4">
          <v-col cols="12" sm="6" md="3">
            <v-card class="rounded-xl pa-3 bg-white border-light" outlined elevation="0">
              <div class="d-flex align-center">
                <v-avatar color="grey lighten-4" size="42" class="mr-3">
                  <v-icon color="grey darken-3">mdi-bug-outline</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                    Total Evaluaciones
                  </div>
                  <div class="text-h5 font-weight-black grey--text text--darken-4">
                    {{ pesticidesList.length }}
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card class="rounded-xl pa-3 bg-white border-light" outlined elevation="0">
              <div class="d-flex align-center">
                <v-avatar color="green lighten-5" size="42" class="mr-3">
                  <v-icon color="green darken-2">mdi-check-decagram</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption text-uppercase font-weight-bold green--text text--darken-3">
                    Aprobados (Libre)
                  </div>
                  <div class="text-h5 font-weight-black green--text text--darken-3">
                    {{ pesticidesKpis.approvedCount }}
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card class="rounded-xl pa-3 bg-white border-light" outlined elevation="0">
              <div class="d-flex align-center">
                <v-avatar color="orange lighten-5" size="42" class="mr-3">
                  <v-icon color="orange darken-3">mdi-alert-circle-outline</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption text-uppercase font-weight-bold orange--text text--darken-3">
                    Restringidos / Rechazados
                  </div>
                  <div class="text-h5 font-weight-black orange--text text--darken-3">
                    {{ pesticidesKpis.restrictedCount }}
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card class="rounded-xl pa-3 bg-white border-light" outlined elevation="0">
              <div class="d-flex align-center">
                <v-avatar color="purple lighten-5" size="42" class="mr-3">
                  <v-icon color="purple darken-2">mdi-beaker-alert-outline</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption text-uppercase font-weight-bold purple--text text--darken-3">
                    Con Trazas Detectadas
                  </div>
                  <div class="text-h5 font-weight-black purple--text text--darken-3">
                    {{ pesticidesKpis.trazasCount }}
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Tabla Pesticidas -->
        <v-card class="rounded-xl border-light bg-white" outlined elevation="0">
          <div class="pa-4 d-flex align-center justify-space-between flex-wrap">
            <div style="max-width: 380px; width: 100%;">
              <v-text-field
                v-model="pesticidesSearch"
                dense
                outlined
                hide-details
                placeholder="Buscar por lote, productor, tipo, sustancia..."
                prepend-inner-icon="mdi-magnify"
                clearable
                class="rounded-lg"
              />
            </div>
            <div class="text-caption grey--text text--darken-2 font-weight-bold mt-2 mt-sm-0">
              Mostrando {{ filteredPesticidesList.length }} de {{ pesticidesList.length }} registros
            </div>
          </div>

          <v-data-table
            :headers="pesticidesHeaders"
            :items="filteredPesticidesList"
            :loading="pesticidesLoading"
            dense
            class="elevation-0 report-table"
            :items-per-page="15"
            :footer-props="{
              'items-per-page-options': [10, 15, 25, 50, 100],
              'show-current-page': true,
            }"
          >
            <!-- Lote -->
            <template v-slot:[`item.FltID`]="{ item }">
              <span class="font-weight-black grey--text text--darken-4">
                {{ item.FltID || '-' }}
              </span>
            </template>

            <!-- Lote MP -->
            <template v-slot:[`item.LoteMp`]="{ item }">
              <span class="font-weight-bold grey--text text--darken-4">
                {{ item.LoteMp || '-' }}
              </span>
            </template>

            <!-- Tipo Sustancia -->
            <template v-slot:[`item.TypeSubstanceName`]="{ item }">
              <v-chip
                x-small
                class="font-weight-bold"
                :color="item.TypeSubstanceName && item.TypeSubstanceName.toUpperCase().includes('TRAZA') ? 'purple lighten-5' : 'blue lighten-5'"
                :text-color="item.TypeSubstanceName && item.TypeSubstanceName.toUpperCase().includes('TRAZA') ? 'purple darken-3' : 'blue darken-3'"
              >
                {{ item.TypeSubstanceName || 'PESTICIDA' }}
              </v-chip>
            </template>

            <!-- Sustancia -->
            <template v-slot:[`item.SubstancesName`]="{ item }">
              <span class="font-weight-bold primary--text">
                {{ item.SubstancesName || '-' }}
              </span>
            </template>

            <!-- Valor RMD -->
            <template v-slot:[`item.RmdValue`]="{ item }">
              <span class="font-weight-bold font-mono">
                {{ formatDecimal(item.RmdValue) }}
              </span>
            </template>

            <!-- Fechas -->
            <template v-slot:[`item.SampleSubmissionDate`]="{ item }">
              {{ formatDate(item.SampleSubmissionDate) }}
            </template>
            <template v-slot:[`item.LaboratoryEntryDate`]="{ item }">
              {{ formatDate(item.LaboratoryEntryDate) }}
            </template>
            <template v-slot:[`item.ProposedResultsDate`]="{ item }">
              {{ formatDate(item.ProposedResultsDate) }}
            </template>
            <template v-slot:[`item.ResultsIssueDate`]="{ item }">
              {{ formatDate(item.ResultsIssueDate) }}
            </template>

            <!-- Entrega -->
            <template v-slot:[`item.Entrega`]="{ item }">
              <v-chip
                x-small
                class="font-weight-bold text-uppercase"
                :color="item.Entrega && item.Entrega.toUpperCase().includes('TIEMPO') ? 'green lighten-5' : 'red lighten-5'"
                :text-color="item.Entrega && item.Entrega.toUpperCase().includes('TIEMPO') ? 'green darken-3' : 'red darken-3'"
              >
                {{ item.Entrega || '-' }}
              </v-chip>
            </template>

            <!-- Estado Dictamen -->
            <template v-slot:[`item.TypeStatusApprovalsName`]="{ item }">
              <v-chip
                small
                class="font-weight-bold text-uppercase"
                :color="getStatusColor(item.TypeStatusApprovalsName).bg"
                :text-color="getStatusColor(item.TypeStatusApprovalsName).text"
              >
                <v-icon left x-small>{{ getStatusColor(item.TypeStatusApprovalsName).icon }}</v-icon>
                {{ formatStatusText(item.TypeStatusApprovalsName) }}
              </v-chip>
            </template>

            <!-- Mercados No Conformes -->
            <template v-slot:[`item.FailedMarkets`]="{ item }">
              <div v-if="item.FailedMarkets" style="min-width: 130px;">
                <v-chip
                  v-for="(market, idx) in parseMarketList(item.FailedMarkets)"
                  :key="idx"
                  x-small
                  color="red lighten-5"
                  text-color="red darken-3"
                  class="font-weight-bold mb-1 mr-1 text-wrap"
                  style="height: auto; padding: 4px 8px;"
                >
                  {{ market }}
                </v-chip>
              </div>
              <span v-else class="grey--text text--lighten-1 font-italic">Ninguno</span>
            </template>

            <!-- Mercados Aptos -->
            <template v-slot:[`item.ApprovedMarkets`]="{ item }">
              <div v-if="item.ApprovedMarkets" style="min-width: 250px;">
                <v-chip
                  v-for="(market, idx) in parseMarketList(item.ApprovedMarkets)"
                  :key="idx"
                  x-small
                  color="green lighten-5"
                  text-color="green darken-3"
                  class="font-weight-bold mb-1 mr-1 text-wrap"
                  style="height: auto; padding: 4px 8px;"
                >
                  {{ market }}
                </v-chip>
              </div>
              <span v-else class="grey--text text--lighten-1 font-italic">Ninguno</span>
            </template>

            <!-- Observación -->
            <template v-slot:[`item.ObservationApproval`]="{ item }">
              <div v-if="item.ObservationApproval" class="grey--text text--darken-2 text-wrap" style="min-width: 260px; font-size: 0.8rem; line-height: 1.35;">
                {{ item.ObservationApproval }}
              </div>
              <span v-else class="grey--text text--lighten-1">-</span>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import _sRawAnalysisService from "@/services/Technicalassistance/TasRawMaterialAnalysisService.js";
import SSelectCampaignArea from "@/components/Utils/SSelectCampaignArea.vue";
import SSelectLot from "@/components/HarvestSchedule/SSelectLot.vue";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";
import SDate from "@/components/Utils/SDate.vue";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";

export default {
  name: "RawMaterialAnalysisReport",
  components: {
    SSelectCampaignArea,
    SSelectLot,
    SSelectDefinition,
    SDate,
  },
  data: () => ({
    activeTab: 0,

    // Estado Metales Pesados
    metalsLoading: false,
    metalsSearch: "",
    metalsFilters: {
      CmaID: null,
      FltID: null,
      TypeCultive: null,
      BeginDate: null,
      EndDate: null,
    },
    metalsList: [],

    // Estado Pesticidas
    pesticidesLoading: false,
    pesticidesSearch: "",
    pesticidesFilters: {
      CmaID: null,
      FltID: null,
      TypeCultive: null,
      BeginDate: null,
      EndDate: null,
    },
    pesticidesList: [],

    // Columnas Metales Pesados
    heavyMetalsHeaders: [
      { text: "Lote", value: "FltID", align: "start", sortable: true, width: "100px" },
      { text: "Campaña", value: "CmaName", sortable: true, width: "140px" },
      { text: "Productor", value: "PrdCardName", sortable: true, width: "180px" },
      { text: "Lote MP", value: "LoteMp", sortable: true, width: "120px" },
      { text: "Homologado", value: "Homologado", sortable: true, width: "120px" },
      { text: "Guía Remisión", value: "GuiaRemision", sortable: true, width: "120px" },
      { text: "Envío Muestra", value: "SampleSubmissionDate", sortable: true, width: "120px" },
      { text: "Ingreso Lab", value: "LaboratoryEntryDate", sortable: true, width: "120px" },
      { text: "Fec. Propuesta", value: "ProposedResultsDate", sortable: true, width: "120px" },
      { text: "Fec. Emisión", value: "ResultsIssueDate", sortable: true, width: "120px" },
      { text: "Días Transc.", value: "ElapsedDays", sortable: true, width: "100px" },
      { text: "Días Retraso", value: "DelayedDays", sortable: true, width: "100px" },
      { text: "Entrega", value: "Entrega", sortable: true, width: "120px" },
      { text: "Sustancia", value: "SubstancesName", sortable: true, width: "140px" },
      { text: "Resultado (ppm)", value: "RmdValue", sortable: true, width: "120px" },
      { text: "Dictamen", value: "TypeStatusApprovalsName", sortable: true, width: "170px" },
      { text: "Mercados No Aptos", value: "FailedMarkets", sortable: false, width: "150px" },
      { text: "Mercados Aptos", value: "ApprovedMarkets", sortable: false, width: "280px" },
      { text: "Observación", value: "ObservationApproval", sortable: false, width: "300px" },
    ],

    // Columnas Pesticidas
    pesticidesHeaders: [
      { text: "Lote", value: "FltID", align: "start", sortable: true, width: "100px" },
      { text: "Campaña", value: "CmaName", sortable: true, width: "140px" },
      { text: "Productor", value: "PrdCardName", sortable: true, width: "180px" },
      { text: "Lote MP", value: "LoteMp", sortable: true, width: "120px" },
      { text: "Homologado", value: "Homologado", sortable: true, width: "120px" },
      { text: "Guía Remisión", value: "GuiaRemision", sortable: true, width: "120px" },
      { text: "Envío Muestra", value: "SampleSubmissionDate", sortable: true, width: "120px" },
      { text: "Ingreso Lab", value: "LaboratoryEntryDate", sortable: true, width: "120px" },
      { text: "Fec. Propuesta", value: "ProposedResultsDate", sortable: true, width: "120px" },
      { text: "Fec. Emisión", value: "ResultsIssueDate", sortable: true, width: "120px" },
      { text: "Días Transc.", value: "ElapsedDays", sortable: true, width: "100px" },
      { text: "Días Retraso", value: "DelayedDays", sortable: true, width: "100px" },
      { text: "Entrega", value: "Entrega", sortable: true, width: "120px" },
      { text: "Tipo", value: "TypeSubstanceName", sortable: true, width: "110px" },
      { text: "Sustancia", value: "SubstancesName", sortable: true, width: "140px" },
      { text: "Resultado (ppm)", value: "RmdValue", sortable: true, width: "120px" },
      { text: "Dictamen", value: "TypeStatusApprovalsName", sortable: true, width: "170px" },
      { text: "Mercados No Aptos", value: "FailedMarkets", sortable: false, width: "150px" },
      { text: "Mercados Aptos", value: "ApprovedMarkets", sortable: false, width: "280px" },
      { text: "Observación", value: "ObservationApproval", sortable: false, width: "300px" },
    ],
  }),

  computed: {
    // Filtrado local Metales
    filteredMetalsList() {
      if (!this.metalsSearch || this.metalsSearch.trim() === "") {
        return this.metalsList;
      }
      const q = this.metalsSearch.trim().toLowerCase();
      return this.metalsList.filter((item) => {
        return (
          String(item.FltID || "").toLowerCase().includes(q) ||
          String(item.CmaName || "").toLowerCase().includes(q) ||
          String(item.PrdCardName || "").toLowerCase().includes(q) ||
          String(item.LoteMp || "").toLowerCase().includes(q) ||
          String(item.SubstancesName || "").toLowerCase().includes(q) ||
          String(item.TypeStatusApprovalsName || "").toLowerCase().includes(q) ||
          String(item.ApprovedMarkets || "").toLowerCase().includes(q) ||
          String(item.FailedMarkets || "").toLowerCase().includes(q)
        );
      });
    },

    // KPIs Metales
    metalsKpis() {
      let approvedCount = 0;
      let restrictedCount = 0;
      let onTimeCount = 0;

      this.metalsList.forEach((item) => {
        const st = String(item.TypeStatusApprovalsName || "").toUpperCase().trim();
        if (st === "2" || st.includes("LIBRE") || st === "APROBADO") {
          approvedCount++;
        } else if (st === "3" || st === "4" || st === "5" || st.includes("RESTRING") || st.includes("OBSERVAD") || st.includes("RECHAZ") || st.includes("NO CONFORME")) {
          restrictedCount++;
        }

        const ent = String(item.Entrega || "").toUpperCase();
        if (ent.includes("TIEMPO")) {
          onTimeCount++;
        }
      });

      return { approvedCount, restrictedCount, onTimeCount };
    },

    // Filtrado local Pesticidas
    filteredPesticidesList() {
      if (!this.pesticidesSearch || this.pesticidesSearch.trim() === "") {
        return this.pesticidesList;
      }
      const q = this.pesticidesSearch.trim().toLowerCase();
      return this.pesticidesList.filter((item) => {
        return (
          String(item.FltID || "").toLowerCase().includes(q) ||
          String(item.CmaName || "").toLowerCase().includes(q) ||
          String(item.PrdCardName || "").toLowerCase().includes(q) ||
          String(item.LoteMp || "").toLowerCase().includes(q) ||
          String(item.TypeSubstanceName || "").toLowerCase().includes(q) ||
          String(item.SubstancesName || "").toLowerCase().includes(q) ||
          String(item.TypeStatusApprovalsName || "").toLowerCase().includes(q) ||
          String(item.ApprovedMarkets || "").toLowerCase().includes(q) ||
          String(item.FailedMarkets || "").toLowerCase().includes(q)
        );
      });
    },

    // KPIs Pesticidas
    pesticidesKpis() {
      let approvedCount = 0;
      let restrictedCount = 0;
      let trazasCount = 0;

      this.pesticidesList.forEach((item) => {
        const st = String(item.TypeStatusApprovalsName || "").toUpperCase().trim();
        if (st === "2" || st.includes("LIBRE") || st === "APROBADO") {
          approvedCount++;
        } else if (st === "3" || st === "4" || st === "5" || st.includes("RESTRING") || st.includes("OBSERVAD") || st.includes("RECHAZ") || st.includes("NO CONFORME")) {
          restrictedCount++;
        }

        const typeSub = String(item.TypeSubstanceName || "").toUpperCase();
        if (typeSub.includes("TRAZA")) {
          trazasCount++;
        }
      });

      return { approvedCount, restrictedCount, trazasCount };
    },
  },

  methods: {
    // Consulta Metales Pesados
    async loadMetalsReport() {
      this.metalsLoading = true;
      try {
        const userId = this.$fun.getUserID();
        const filterPayload = {
          CmaID: this.metalsFilters.CmaID || null,
          FltID: this.metalsFilters.FltID || null,
          TypeCultive: this.metalsFilters.TypeCultive || null,
          BeginDate: this.metalsFilters.BeginDate ? moment(this.metalsFilters.BeginDate).format("YYYY-MM-DD") : null,
          EndDate: this.metalsFilters.EndDate ? moment(this.metalsFilters.EndDate).format("YYYY-MM-DD") : null,
        };

        const resp = await _sRawAnalysisService.heavyMetalsAnalysisReport(filterPayload, userId);
        if (resp && resp.data) {
          this.metalsList = resp.data;
        } else {
          this.metalsList = [];
        }
      } catch (error) {
        console.error("Error al consultar reporte de metales:", error);
        this.$fun.alert("Error al consultar el reporte de metales pesados", "error");
      } finally {
        this.metalsLoading = false;
      }
    },

    // Consulta Pesticidas
    async loadPesticidesReport() {
      this.pesticidesLoading = true;
      try {
        const userId = this.$fun.getUserID();
        const filterPayload = {
          CmaID: this.pesticidesFilters.CmaID || null,
          FltID: this.pesticidesFilters.FltID || null,
          TypeCultive: this.pesticidesFilters.TypeCultive || null,
          BeginDate: this.pesticidesFilters.BeginDate ? moment(this.pesticidesFilters.BeginDate).format("YYYY-MM-DD") : null,
          EndDate: this.pesticidesFilters.EndDate ? moment(this.pesticidesFilters.EndDate).format("YYYY-MM-DD") : null,
        };

        const resp = await _sRawAnalysisService.pesticidesAnalysisReport(filterPayload, userId);
        if (resp && resp.data) {
          this.pesticidesList = resp.data;
        } else {
          this.pesticidesList = [];
        }
      } catch (error) {
        console.error("Error al consultar reporte de pesticidas:", error);
        this.$fun.alert("Error al consultar el reporte de pesticidas y trazas", "error");
      } finally {
        this.pesticidesLoading = false;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      return moment(dateStr).format("DD/MM/YYYY");
    },

    formatDecimal(val) {
      if (val === null || val === undefined || isNaN(val)) return "-";
      return Number(val).toFixed(6);
    },

    formatStatusText(val) {
      if (val === 2 || val === "2") return "APROBADO";
      if (val === 3 || val === "3") return "RESTRINGIDO";
      if (val === 4 || val === "4") return "NO CONFORME";
      if (val === 1 || val === "1") return "PENDIENTE";
      return val || "PENDIENTE";
    },

    parseMarketList(str) {
      if (!str || str === null || str === undefined) return [];
      const s = String(str).trim();
      if (s === "" || s.toUpperCase() === "NULL") return [];
      return s
        .split(",")
        .map((m) => m.trim())
        .filter((m) => m.length > 0 && m.toUpperCase() !== "NULL");
    },

    getStatusColor(statusName) {
      const st = String(statusName || "").toUpperCase().trim();
      if (st === "2" || st.includes("LIBRE") || st === "APROBADO") {
        return { bg: "green lighten-5", text: "green darken-3", icon: "mdi-check-circle" };
      }
      if (st.includes("OBSERVAD")) {
        return { bg: "purple lighten-5", text: "purple darken-3", icon: "mdi-clock-alert-outline" };
      }
      if (st === "3" || st.includes("RESTRING")) {
        return { bg: "orange lighten-5", text: "orange darken-3", icon: "mdi-shield-alert-outline" };
      }
      if (st === "4" || st === "5" || st.includes("RECHAZAD") || st.includes("NO CONFORME")) {
        return { bg: "red lighten-5", text: "red darken-3", icon: "mdi-close-circle" };
      }
      return { bg: "blue-grey lighten-5", text: "blue-grey darken-3", icon: "mdi-clock-outline" };
    },

    exportMetalsExcel() {
      this.generateExcelFile("Metales_Pesados", "METALES PESADOS", this.heavyMetalsHeaders, this.filteredMetalsList);
    },

    exportPesticidesExcel() {
      this.generateExcelFile("Pesticidas_y_Trazas", "PESTICIDAS Y TRAZAS", this.pesticidesHeaders, this.filteredPesticidesList);
    },

    async generateExcelFile(sheetName, titleText, columns, dataList) {
      if (!dataList || dataList.length === 0) {
        this.$fun.alert("No hay registros para exportar", "warning");
        return;
      }

      try {
        const workbook = new ExcelJS.Workbook();
        workbook.creator = "SunExpert";
        const worksheet = workbook.addWorksheet(sheetName);

        // Title row
        worksheet.mergeCells(1, 1, 1, columns.length);
        const titleCell = worksheet.getCell(1, 1);
        titleCell.value = `REPORTE DE ANÁLISIS DE MATERIA PRIMA - ${titleText}`;
        titleCell.font = { name: "Calibri", size: 12, bold: true, color: { argb: "FFFFFFFF" } };
        titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1565C0" } };
        titleCell.alignment = { vertical: "middle", horizontal: "center" };
        worksheet.getRow(1).height = 28;

        // Headers row
        const headersRowValues = columns.map((c) => c.text.toUpperCase());
        const headerRow = worksheet.addRow(headersRowValues);
        headerRow.height = 22;

        headerRow.eachCell((cell) => {
          cell.font = { name: "Calibri", size: 9, bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF37474F" } };
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.border = {
            top: { style: "thin", color: { argb: "FFB0BEC5" } },
            left: { style: "thin", color: { argb: "FFB0BEC5" } },
            bottom: { style: "thin", color: { argb: "FFB0BEC5" } },
            right: { style: "thin", color: { argb: "FFB0BEC5" } },
          };
        });

        // Rows
        dataList.forEach((item) => {
          const rowValues = columns.map((col) => {
            const val = item[col.value];
            if (col.value.includes("Date")) {
              return val ? moment(val).format("DD/MM/YYYY") : "";
            }
            if (col.value === "RmdValue") {
              return val !== null && val !== undefined ? Number(val) : "";
            }
            return val !== null && val !== undefined ? val : "";
          });
          const row = worksheet.addRow(rowValues);
          row.eachCell((cell) => {
            cell.font = { name: "Calibri", size: 9 };
            cell.alignment = { vertical: "middle" };
            cell.border = {
              top: { style: "thin", color: { argb: "FFE0E0E0" } },
              left: { style: "thin", color: { argb: "FFE0E0E0" } },
              bottom: { style: "thin", color: { argb: "FFE0E0E0" } },
              right: { style: "thin", color: { argb: "FFE0E0E0" } },
            };
          });
        });

        // Auto width
        worksheet.columns.forEach((column) => {
          let maxLength = 12;
          column.eachCell({ includeEmpty: true }, (cell) => {
            const cellLength = cell.value ? String(cell.value).length : 0;
            if (cellLength > maxLength) {
              maxLength = Math.min(cellLength, 45);
            }
          });
          column.width = maxLength + 3;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, `Reporte_${sheetName}_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
        this.$fun.alert("Archivo Excel generado con éxito", "success");
      } catch (err) {
        console.error("Error al exportar Excel:", err);
        this.$fun.alert("Error al generar el archivo Excel", "error");
      }
    },
  },
};
</script>

<style scoped>
.border-light {
  border: 1px solid #e2e8f0 !important;
}

.report-table >>> th {
  font-weight: 700 !important;
  color: #1e293b !important;
  background-color: #f8fafc !important;
  font-size: 0.78rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e2e8f0 !important;
}

.report-table >>> td {
  font-size: 0.82rem !important;
  border-bottom: 1px solid #f1f5f9 !important;
  white-space: normal !important;
  height: auto !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
  vertical-align: middle !important;
}

.report-table >>> tr {
  height: auto !important;
}

.font-mono {
  font-family: Consolas, "Courier New", monospace !important;
}

.text-wrap {
  white-space: normal !important;
  word-break: break-word !important;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
